import { Provider } from '~/models/Provider'
import { TranslationService } from '../Translation'
import { BaseNavigation } from './baseNavigation'
import Settings from '../settings'

export class TripNavigation extends BaseNavigation {
	static tripRoute: string = '/trip'

	static toCreateTrip(destinationId?: string, spotIds?: string[]): void {
		const locale = TranslationService.getInstance().getLocale()
		const params = new URLSearchParams()
		if (destinationId) params.set('id', destinationId)
		if (spotIds?.length) params.set('spotIds', spotIds.join(','))
		const queryString = params.toString()
		const route = queryString ? `/${locale}/trip/create?${queryString}` : `/${locale}/trip/create`
		this.navigateTo(route)
	}

	static toTripSpots(itineraryId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${itineraryId}/spots`
		this.navigateTo(route)
	}

	static toTripTasks(itineraryId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${itineraryId}/tasks`
		this.navigateTo(route)
	}

	static tripDashboardUrl(itineraryId: string): string {
		const translation = TranslationService.getInstance()
		return `/${translation.getLocale()}/trip/${itineraryId}/dashboard`
	}

	static toTripDashboard(itineraryId: string): void {
		const route = this.tripDashboardUrl(itineraryId)
		this.navigateTo(route)
	}

	static toTripDashboardOnboarding(itineraryId: string): void {
		const route = this.tripDashboardUrl(itineraryId) + '?onboarding=true'
		this.navigateTo(route)
	}

	static toTripItinerary(itineraryId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${itineraryId}/itinerary`
		this.navigateTo(route)
	}

	static getTripProductsStoreUrl(itineraryId: string): string {
		const locale = TranslationService.getInstance().getLocale()
		return `/${locale}/trip/${itineraryId}/store/ideas`
	}

	static toTripProductStore(itineraryId: string): void {
		const route = this.getTripProductsStoreUrl(itineraryId)
		this.navigateTo(route)
	}

	static getTripActivitiesStoreUrl(itineraryId: string): string {
		const locale = TranslationService.getInstance().getLocale()
		return `/${locale}/trip/${itineraryId}/store/activities`
	}

	static toTripActivitiesStore(itineraryId: string): void {
		const route = this.getTripActivitiesStoreUrl(itineraryId)
		this.navigateTo(route)
	}

	static toTripIndispensablesStore(itineraryId: string): void {
		const route = `/${TranslationService.getInstance().getLocale()}/trip/${itineraryId}/store/indispensables`
		this.navigateTo(route)
	}

	static toTripDocuments(itineraryId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${itineraryId}/documents`
		this.navigateTo(route)
	}

	static toTripWall(itineraryId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${itineraryId}/wall`
		this.navigateTo(route)
	}

	static toThanksPage(tripId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${tripId}/thank_you`
		this.navigateTo(route)
	}

	static getTripProductUrl(baseProductUrl: string): string {
		const url = new URL(baseProductUrl)
		url.searchParams.append('utm_source', 'product')
		url.searchParams.append('utm_medium', 'planner')
		return url.toString()
	}

	static toTripInvitation(tripUrl: string): void {
		const url = new URL(tripUrl)
		const locale = TranslationService.getInstance().getLocale()
		const route: string = `/${locale}${url.pathname}${url.search}`
		this.navigateTo(route)
	}

	static toTripStoreTripProduct(itineraryId: string, productId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${itineraryId}/store/ideas/${productId}`
		this.navigateTo(route)
	}

	static toTripProduct(productUrl: string): void {
		const url = this.getTripProductUrl(productUrl)
		this.navigateToExternalTab(url)
	}

	static toExternalProductWithParams(productUrl: string, params: Record<string, string>): void {
		const url = new URL(productUrl)
		Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value))
		this.navigateToExternalTab(url.toString())
	}

	static toTripStoreActivityProduct(itineraryId: string, productId: string): void {
		const locale = TranslationService.getInstance().getLocale()
		const route = `/${locale}/trip/${itineraryId}/store/activities/${productId}`
		this.navigateTo(route)
	}

	static toActivityProduct(productUrl: string, provider: Provider, tripId: string): void {
		this.navigateToExternalTab(this.getExternalUrl(productUrl, provider, tripId))
	}

	private static getExternalUrl(externalUrl: string, provider: Provider, tripId: string): string {
		const isCivitatisProvider: boolean = provider?.id == Settings.civitatisProviderId()
		if (!isCivitatisProvider) return externalUrl
		return this.buildCivitatisExternalUrl(externalUrl, tripId)
	}

	private static buildCivitatisExternalUrl(externalUrl: string, tripId: string): string {
		const url = new URL(externalUrl)
		url.searchParams.set('aid', Settings.civitatisAffiliate())
		url.searchParams.set('cmp', 'producto_web')
		url.searchParams.set('cmpint', tripId)
		return url.toString()
	}
}
